import { Drawer, Pagination } from '@/components/commons';
import { KazokugotoService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import { IResponseError } from '@/types/common';
import { IKazokugotoItem, KazokugotoEdition } from '@/types/response';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Image,
  Select,
  Space,
  Spin,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { GenerationLogSection } from './GenerationLogSection';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const EDITION_LABEL: Record<KazokugotoEdition, string> = {
  morning: '朝刊',
  evening: '夕刊',
  manzai: '漫才',
};

const EDITION_COLOR: Record<KazokugotoEdition, string> = {
  morning: 'gold',
  evening: 'purple',
  manzai: 'cyan',
};

const STATUS_COLOR: Record<string, string> = {
  published: 'green',
  ready: 'blue',
  draft: 'default',
  retired: 'red',
};

interface IFilters {
  page: number;
  limit: number;
  edition?: KazokugotoEdition;
  status?: 'draft' | 'ready' | 'published' | 'retired';
  from?: string;
  to?: string;
}

interface IProps {
  title?: string;
}

export const KazokugotoItemList: React.FC<IProps> = ({ title }) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<IFilters>({ page: 1, limit: 20 });
  const [detailItem, setDetailItem] = useState<IKazokugotoItem | null>(null);

  const { data, isFetching } = useQuery(
    [ENDPOINT.KAZOKUGOTO.LIST_ITEMS, filters],
    () => KazokugotoService.listItems(filters),
    {
      // カバー生成中の item があれば 10 秒ごとにポーリングして完了を検知
      // （カバー生成は通常 30 秒〜1 分かかるため間隔は長めで十分）
      refetchInterval: (resp) =>
        resp?.data.data.some((i) => i.coverStatus === 'generating')
          ? 10000
          : false,
    },
  );

  // 生成ログ（web 検索の参照リンク）は一覧に含まれないため Drawer を開いたときに個別取得
  const { data: genLogData, isFetching: isGenLogFetching } = useQuery(
    [ENDPOINT.KAZOKUGOTO.GET_ITEM_GENERATION_LOG, detailItem?.id],
    () => KazokugotoService.getItemGenerationLog(detailItem!.id),
    { enabled: !!detailItem },
  );

  // ポーリングで取得した最新 item を詳細 Drawer にも反映（生成中→完了の反映）
  useEffect(() => {
    if (!detailItem || !data?.data.data) return;
    const fresh = data.data.data.find((i) => i.id === detailItem.id);
    if (
      fresh &&
      (fresh.coverStatus !== detailItem.coverStatus ||
        fresh.coverImageUrl !== detailItem.coverImageUrl)
    ) {
      setDetailItem(fresh);
    }
  }, [data, detailItem]);

  const regenerateCoverMutation = useMutation(
    (itemId: number) => KazokugotoService.regenerateCover(itemId),
    {
      onSuccess: (response) => {
        notificationMessage({
          message: `カバー画像の生成を開始しました (id=${response.data.id})。完了まで少しお待ちください`,
          type: NOTIFY_STATUS.SUCCESS,
        });
        // generating 状態が返るので一覧を更新 → refetchInterval がポーリング開始
        queryClient.invalidateQueries([ENDPOINT.KAZOKUGOTO.LIST_ITEMS]);
        if (detailItem && detailItem.id === response.data.id) {
          setDetailItem({ ...detailItem, coverStatus: 'generating' });
        }
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `カバー再生成の開始に失敗: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const updateStatusMutation = useMutation(
    (params: { id: number; status: IFilters['status'] }) =>
      KazokugotoService.updateItemStatus(params.id, {
        status: params.status as NonNullable<IFilters['status']>,
      }),
    {
      onSuccess: (response) => {
        notificationMessage({
          message: `ステータスを ${response.data.status} に変更しました`,
          type: NOTIFY_STATUS.SUCCESS,
        });
        queryClient.invalidateQueries([ENDPOINT.KAZOKUGOTO.LIST_ITEMS]);
        if (detailItem && detailItem.id === response.data.id) {
          setDetailItem({ ...detailItem, status: response.data.status });
        }
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `ステータス変更に失敗: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const handleFilterEdition = (edition?: KazokugotoEdition) => {
    setFilters((f) => ({ ...f, page: 1, edition }));
  };

  const handleFilterStatus = (status?: IFilters['status']) => {
    setFilters((f) => ({ ...f, page: 1, status }));
  };

  const handleFilterDateRange = (
    range: [Dayjs | null, Dayjs | null] | null,
  ) => {
    setFilters((f) => ({
      ...f,
      page: 1,
      from: range?.[0]?.format('YYYY-MM-DD'),
      to: range?.[1]?.format('YYYY-MM-DD'),
    }));
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters((f) => ({ ...f, page, limit: pageSize }));
  };

  const columns: TableColumnsType<IKazokugotoItem> = [
    {
      title: '配信日',
      dataIndex: 'publishDate',
      width: 110,
      render: (d: string) => <Text strong>{d}</Text>,
    },
    {
      title: 'edition',
      dataIndex: 'edition',
      width: 90,
      render: (e: KazokugotoEdition) => (
        <Tag color={EDITION_COLOR[e]}>{EDITION_LABEL[e]}</Tag>
      ),
    },
    {
      title: 'cover',
      dataIndex: 'coverImageUrl',
      width: 110,
      render: (url: string | null | undefined, record) => {
        if (record.coverStatus === 'generating') {
          return (
            <Space size={4}>
              <Spin size='small' />
              <Text type='secondary' style={{ fontSize: 11 }}>
                生成中
              </Text>
            </Space>
          );
        }
        if (url) {
          return (
            // 画像クリックで行の onClick が発火しないよう伝播を止める
            <span
              onClick={(e) => e.stopPropagation()}
              style={{ display: 'inline-block' }}
            >
              <Image
                src={url}
                width={80}
                height={45}
                alt='cover'
                style={{ objectFit: 'cover', borderRadius: 4 }}
                preview={{ mask: '拡大' }}
              />
            </span>
          );
        }
        if (record.coverStatus === 'failed') {
          return <Tag color='red'>失敗</Tag>;
        }
        return (
          <Text type='secondary' style={{ fontSize: 11 }}>
            (なし)
          </Text>
        );
      },
    },
    {
      title: 'タイトル',
      dataIndex: 'title',
      ellipsis: true,
    },
    {
      title: 'status',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => <Tag color={STATUS_COLOR[s]}>{s}</Tag>,
    },
    {
      title: 'id',
      dataIndex: 'id',
      width: 70,
      render: (id: number) => (
        <Text type='secondary' style={{ fontSize: 12 }}>
          #{id}
        </Text>
      ),
    },
    {
      title: '操作',
      width: 130,
      render: (_, record) => {
        const generating = record.coverStatus === 'generating';
        return (
          <Button
            size='small'
            disabled={generating}
            onClick={(e) => {
              e.stopPropagation();
              regenerateCoverMutation.mutate(record.id);
            }}
            loading={
              regenerateCoverMutation.isLoading &&
              regenerateCoverMutation.variables === record.id
            }
          >
            {generating ? '生成中…' : 'カバー再生成'}
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header>
          <Title level={3} style={{ marginTop: 0 }}>
            かぞくごと。アイテム一覧
          </Title>
          <Paragraph type='secondary'>
            生成済みの朝刊・夕刊・漫才の一覧です。行をクリックで詳細、操作からカバー画像のみ再生成できます。
          </Paragraph>
        </header>

        <Card
          size='small'
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
          styles={{ header: { background: '#fff' }, body: { background: '#fff' } }}
        >
          <Form layout='inline'>
            <Form.Item label='edition'>
              <Select
                allowClear
                placeholder='全て'
                style={{ width: 140 }}
                value={filters.edition}
                onChange={(v) => handleFilterEdition(v)}
                options={[
                  { value: 'morning', label: '朝刊' },
                  { value: 'evening', label: '夕刊' },
                  { value: 'manzai', label: '漫才' },
                ]}
              />
            </Form.Item>
            <Form.Item label='status'>
              <Select
                allowClear
                placeholder='全て'
                style={{ width: 140 }}
                value={filters.status}
                onChange={(v) => handleFilterStatus(v)}
                options={[
                  { value: 'published', label: 'published' },
                  { value: 'ready', label: 'ready' },
                  { value: 'draft', label: 'draft' },
                  { value: 'retired', label: 'retired' },
                ]}
              />
            </Form.Item>
            <Form.Item label='配信日'>
              <RangePicker
                format='YYYY-MM-DD'
                value={
                  filters.from && filters.to
                    ? [dayjs(filters.from), dayjs(filters.to)]
                    : undefined
                }
                onChange={(range) =>
                  handleFilterDateRange(range as [Dayjs | null, Dayjs | null])
                }
              />
            </Form.Item>
          </Form>
        </Card>

        <Card
          size='small'
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
          styles={{ header: { background: '#fff' }, body: { background: '#fff' } }}
        >
          <Table<IKazokugotoItem>
            rowKey='id'
            loading={isFetching}
            columns={columns}
            dataSource={data?.data.data ?? []}
            pagination={false}
            scroll={{ x: 900 }}
            onRow={(record) => ({
              onClick: () => setDetailItem(record),
              style: { cursor: 'pointer' },
            })}
            locale={{ emptyText: '該当アイテムはありません' }}
          />
          {(data?.data.pagination?.total ?? 0) > 0 && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Pagination
                total={data?.data.pagination.total}
                pageSize={filters.limit}
                current={filters.page}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                onChange={handlePageChange}
                onShowSizeChange={handlePageChange}
              />
            </div>
          )}
        </Card>
      </div>

      {/* 詳細 Drawer */}
      <Drawer
        title={
          detailItem
            ? `${detailItem.publishDate} / ${EDITION_LABEL[detailItem.edition]} / id=${detailItem.id}`
            : ''
        }
        open={!!detailItem}
        onCancel={() => setDetailItem(null)}
        onlyCancel
        cancelText='閉じる'
        width={1000}
      >
        {detailItem && (
          <Space direction='vertical' size={16} style={{ width: '100%' }}>
            {detailItem.coverStatus === 'generating' ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
                <div style={{ marginTop: 8 }}>
                  <Text type='secondary'>カバー画像を生成中…（完了まで少しお待ちください）</Text>
                </div>
              </div>
            ) : detailItem.coverImageUrl ? (
              <div style={{ textAlign: 'center' }}>
                <Image
                  src={detailItem.coverImageUrl}
                  alt='cover'
                  style={{
                    maxWidth: '100%',
                    maxHeight: 480,
                    borderRadius: 6,
                  }}
                />
              </div>
            ) : detailItem.coverStatus === 'failed' ? (
              <Alert
                type='error'
                showIcon
                message='カバー画像の生成に失敗しました'
                description={detailItem.coverError ?? undefined}
              />
            ) : (
              <Text type='secondary'>cover image なし</Text>
            )}

            <div>
              <Title level={4} style={{ marginTop: 0 }}>
                {detailItem.title}
              </Title>
              {detailItem.subtitle && (
                <Paragraph type='secondary'>{detailItem.subtitle}</Paragraph>
              )}
              <Space wrap size={[8, 4]}>
                <Tag color={EDITION_COLOR[detailItem.edition]}>
                  {EDITION_LABEL[detailItem.edition]}
                </Tag>
                <Tag color={STATUS_COLOR[detailItem.status]}>
                  {detailItem.status}
                </Tag>
                <Text type='secondary' style={{ fontSize: 12 }}>
                  topic id={detailItem.topicId}
                </Text>
                <Text type='secondary' style={{ fontSize: 12 }}>
                  generatorVersion: {detailItem.generatorVersion}
                </Text>
                {detailItem.ayumiId && (
                  <Text type='secondary' style={{ fontSize: 12 }}>
                    ayumiId: {detailItem.ayumiId}
                  </Text>
                )}
                {detailItem.coverImageId !== undefined &&
                  detailItem.coverImageId !== null && (
                    <Text type='secondary' style={{ fontSize: 12 }}>
                      coverImageId: {detailItem.coverImageId}
                    </Text>
                  )}
              </Space>
            </div>

            <Space wrap size={12} align='center'>
              <Button
                type='primary'
                disabled={detailItem.coverStatus === 'generating'}
                onClick={() => regenerateCoverMutation.mutate(detailItem.id)}
                loading={
                  regenerateCoverMutation.isLoading &&
                  regenerateCoverMutation.variables === detailItem.id
                }
              >
                {detailItem.coverStatus === 'generating'
                  ? 'カバー画像を生成中…'
                  : 'カバー画像を再生成'}
              </Button>
              <Space size={8} align='center'>
                <Text strong>status:</Text>
                <Select
                  value={detailItem.status}
                  style={{ width: 160 }}
                  loading={
                    updateStatusMutation.isLoading &&
                    updateStatusMutation.variables?.id === detailItem.id
                  }
                  onChange={(v) =>
                    updateStatusMutation.mutate({
                      id: detailItem.id,
                      status: v as IFilters['status'],
                    })
                  }
                  options={[
                    { value: 'published', label: 'published（公開）' },
                    { value: 'ready', label: 'ready（準備完了）' },
                    { value: 'draft', label: 'draft（下書き）' },
                    { value: 'retired', label: 'retired（非公開）' },
                  ]}
                />
              </Space>
            </Space>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                body
              </Text>
              <pre
                style={{
                  background: '#fafafa',
                  padding: 12,
                  borderRadius: 4,
                  fontSize: 12,
                  maxHeight: 500,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0,
                }}
              >
                {JSON.stringify(detailItem.body, null, 2)}
              </pre>
            </div>

            <GenerationLogSection
              loading={isGenLogFetching}
              data={genLogData?.data}
            />

            {detailItem.meta && Object.keys(detailItem.meta).length > 0 && (
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  meta
                </Text>
                <pre
                  style={{
                    background: '#fafafa',
                    padding: 12,
                    borderRadius: 4,
                    fontSize: 12,
                    maxHeight: 300,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: 0,
                  }}
                >
                  {JSON.stringify(detailItem.meta, null, 2)}
                </pre>
              </div>
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
};
